const dbg=(...a)=>{if(typeof window!=="undefined"&&window.__XTRC_DEBUG)console["log"](...a)};
// ----------------------------------------------------------------------------
// media-cache.cjs — IndexedDB-backed media download cache for all adapters
// ----------------------------------------------------------------------------
// Prevents re-downloading files that have already been fetched.
// Stores downloaded media as Blobs in IndexedDB, keyed by URL.
//
// API:
//   loadWithCache(url, { onProgress, signal, responseType }) → ArrayBuffer
//   getCached(url) → { blob, size, timestamp } | null
//   cacheMedia(url, data, metadata) → void
//   removeCached(url) → void
//   clearCache() → void
//   getCacheSize() → number
// ----------------------------------------------------------------------------

const DB_NAME = 'xtrc-media-cache';
const DB_VERSION = 1;
const STORE_NAME = 'media';

// Default cache cap (conservative — works without persistent storage).
// Dynamically raised if the user has granted persistent storage.
const DEFAULT_MAX_CACHE_SIZE = 500 * 1024 * 1024;       // 500MB
const PERSISTED_MAX_CACHE_SIZE = 4 * 1024 * 1024 * 1024; // 4GB with persistent storage
let MAX_CACHE_SIZE = DEFAULT_MAX_CACHE_SIZE;

let dbPromise = null;

// Probe persistent storage status and adjust the cache cap accordingly.
// With persistent storage, browsers grant a much larger quota (often 50%+ of disk).
async function _probeStorageQuota() {
   try {
      if (!navigator.storage || !navigator.storage.persisted) return;
      const persisted = await navigator.storage.persisted();
      if (!persisted) return;
      if (navigator.storage.estimate) {
         const est = await navigator.storage.estimate();
         if (est && est.quota) {
            // Use up to 40% of the available quota for the media cache,
            // leaving room for the bucket and other IDB databases.
            const dynamic = Math.floor(est.quota * 0.4);
            MAX_CACHE_SIZE = Math.min(dynamic, PERSISTED_MAX_CACHE_SIZE);
            dbg(`[media-cache] persistent storage: quota=${(est.quota / 1024 / 1024 / 1024).toFixed(1)}GB, cache cap=${(MAX_CACHE_SIZE / 1024 / 1024).toFixed(0)}MB`);
         } else {
            MAX_CACHE_SIZE = PERSISTED_MAX_CACHE_SIZE;
         }
      } else {
         MAX_CACHE_SIZE = PERSISTED_MAX_CACHE_SIZE;
      }
   } catch (_) {}
}
_probeStorageQuota().catch(() => {});

function openDB() {
   if (dbPromise) return dbPromise;
   dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
         const db = e.target.result;
         if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
            store.createIndex('timestamp', 'timestamp', { unique: false });
         }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
   });
   return dbPromise;
}

/**
 * Simple hash function for cache keys (FNV-1a).
 * Used to generate short keys from URLs.
 */
function hashUrl(url) {
   let hash = 0x811c9dc5;
   for (let i = 0; i < url.length; i++) {
      hash ^= url.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
   }
   return hash.toString(36);
}

/**
 * Check if a URL is cached.
 * @param {string} url
 * @returns {Promise<{blob: Blob, size: number, timestamp: number, filename: string} | null>}
 */
async function getCached(url) {
   try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readonly');
         const store = tx.objectStore(STORE_NAME);
         const req = store.get(url);
         req.onsuccess = () => resolve(req.result || null);
         req.onerror = () => reject(req.error);
      });
   } catch (e) {
      return null;
   }
}

/**
 * Store media data in the cache.
 * @param {string} url
 * @param {ArrayBuffer|Blob|Uint8Array} data
 * @param {object} metadata - { filename, adapterType, size }
 */
async function cacheMedia(url, data, metadata = {}) {
   try {
      const db = await openDB();
      const blob = data instanceof Blob ? data : new Blob([data]);
      const record = {
         url,
         blob,
         size: blob.size,
         timestamp: Date.now(),
         filename: metadata.filename || '',
         adapterType: metadata.adapterType || '',
      };
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readwrite');
         const store = tx.objectStore(STORE_NAME);
         store.put(record);
         tx.oncomplete = () => {
            dbg(`[media-cache] Cached ${url} (${(blob.size / 1024 / 1024).toFixed(1)}MB)`);
            resolve();
         };
         tx.onerror = () => reject(tx.error);
      });
   } catch (e) {
   }
}

/**
 * Download a URL with cache support.
 * If the URL is already cached, returns the cached data immediately.
 * Otherwise, downloads with XHR (for progress tracking), caches the result,
 * and returns the ArrayBuffer.
 *
 * @param {string} url - URL to download
 * @param {object} opts - { onProgress(percent, message), signal, headers }
 * @returns {Promise<ArrayBuffer>}
 */
async function loadWithCache(url, opts = {}) {
   const { onProgress, signal, headers } = opts;

   // Skip cache for blob: URLs (temporary, from loadFromFile)
   const isBlobUrl = typeof url === 'string' && url.startsWith('blob:');

   // 1. Check cache first (skip for blob URLs)
   if (!isBlobUrl) {
      const cached = await getCached(url);
      if (cached && cached.blob) {
         dbg(`[media-cache] Cache HIT for ${url} (${(cached.blob.size / 1024 / 1024).toFixed(1)}MB)`);
         if (onProgress) onProgress(100, 'Loaded from cache');
         const buf = await cached.blob.arrayBuffer();
         return buf;
      }
   }

   // 2. Download with XHR (supports progress tracking)
   dbg(`[media-cache] Cache MISS for ${url}, downloading...`);
   return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';

      if (headers) {
         for (const [k, v] of Object.entries(headers)) {
            xhr.setRequestHeader(k, v);
         }
      }

      xhr.onprogress = (event) => {
         if (signal && signal.aborted) {
            xhr.abort();
            reject(new Error('Load cancelled'));
            return;
         }
         if (event.lengthComputable && onProgress) {
            const pct = (event.loaded / event.total) * 100;
            const mb = (event.loaded / 1024 / 1024).toFixed(1);
            const mt = (event.total / 1024 / 1024).toFixed(1);
            onProgress(pct, `Downloading ${mb}/${mt} MB`);
         }
      };

      xhr.onload = () => {
         if (xhr.status >= 200 && xhr.status < 300) {
            const buf = xhr.response;
            // Cache the downloaded data (skip for blob URLs)
            if (!isBlobUrl) {
               cacheMedia(url, buf, { size: buf.byteLength }).catch(() => {});
            }
            if (onProgress) onProgress(100, 'Download complete');
            resolve(buf);
         } else {
            reject(new Error(`HTTP ${xhr.status}`));
         }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.onabort = () => reject(new Error('Load cancelled'));

      if (signal) {
         signal.addEventListener('abort', () => {
            xhr.abort();
         }, { once: true });
      }

      xhr.send();
   });
}

/**
 * Remove a specific URL from the cache.
 */
async function removeCached(url) {
   try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readwrite');
         const store = tx.objectStore(STORE_NAME);
         store.delete(url);
         tx.oncomplete = () => resolve();
         tx.onerror = () => reject(tx.error);
      });
   } catch (e) {
   }
}

/**
 * Clear the entire media cache.
 */
async function clearCache() {
   try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readwrite');
         const store = tx.objectStore(STORE_NAME);
         store.clear();
         tx.oncomplete = () => {
            dbg('[media-cache] Cache cleared');
            resolve();
         };
         tx.onerror = () => reject(tx.error);
      });
   } catch (e) {
   }
}

/**
 * Get total cache size and entry count.
 * @returns {Promise<{size: number, count: number}>}
 */
async function getCacheInfo() {
   try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readonly');
         const store = tx.objectStore(STORE_NAME);
         const req = store.getAll();
         req.onsuccess = () => {
            const records = req.result || [];
            const size = records.reduce((sum, r) => sum + (r.size || 0), 0);
            resolve({ size, count: records.length });
         };
         req.onerror = () => reject(req.error);
      });
   } catch (e) {
      return { size: 0, count: 0 };
   }
}

/**
 * Evict oldest entries if cache exceeds MAX_CACHE_SIZE.
 */
async function evictIfNeeded() {
   try {
      const info = await getCacheInfo();
      if (info.size <= MAX_CACHE_SIZE) return;

      const db = await openDB();
      const records = await new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readonly');
         const store = tx.objectStore(STORE_NAME);
         const req = store.getAll();
         req.onsuccess = () => resolve(req.result || []);
         req.onerror = () => reject(req.error);
      });

      // Sort by timestamp ascending (oldest first)
      records.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

      let currentSize = info.size;
      const toDelete = [];
      for (const r of records) {
         if (currentSize <= MAX_CACHE_SIZE * 0.8) break; // Evict down to 80%
         toDelete.push(r.url);
         currentSize -= (r.size || 0);
      }

      if (toDelete.length > 0) {
         dbg(`[media-cache] Evicting ${toDelete.length} old entries (${((info.size - currentSize) / 1024 / 1024).toFixed(1)}MB)`);
         const tx = db.transaction(STORE_NAME, 'readwrite');
         const store = tx.objectStore(STORE_NAME);
         for (const url of toDelete) {
            store.delete(url);
         }
         await new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
         });
      }
   } catch (e) {
   }
}

// Run eviction on module load and periodically
evictIfNeeded().catch(() => {});

module.exports = {
   loadWithCache,
   getCached,
   cacheMedia,
   removeCached,
   clearCache,
   getCacheInfo,
   evictIfNeeded,
};
